import _extends from "@babel/runtime/helpers/esm/extends";
import React, { Fragment, useLayoutEffect, useMemo, useRef } from 'react';
import { useCombinedRef } from '../../hooks';
import { useDesignSystemTheme } from '../../providers/DesignSystemThemeProvider';
import { createDataTestId, percent, px } from '../../utils';
import { Amount } from '../Amount';
import { StyleType } from '../Amount/Amount.constants';
import { Icon } from '../Icon';
import { Text } from '../Text';
import { Circle } from './Circle';
import { DynamicValues, ProgressColor, ProgressSize, SizeToStyle } from './Progress.constants';
import { ProgressBackground, ProgressRoot, ProgressValue, ProgressValueCirlceLabel, ProgressValueLabel } from './Progress.styles';
function getTranslateXTransformForValue(value, max) {
  return percent(Math.floor((1 - value / max) * 100) * -1);
}
export const Progress = React.forwardRef(function Progress(_ref, ref) {
  let {
    max = 100,
    color = ProgressColor.SECONDARY_CONTRAST,
    size = ProgressSize.DEFAULT,
    valueColor,
    value,
    isCountdown = false,
    isCircle = false,
    isValueVisible = false,
    isDynamicColor = false,
    children = null,
    iconComponent = null,
    testId,
    ...restProgressProps
  } = _ref;
  const progressRef = useRef(null);
  const progressValueRef = useRef(null);
  const progressValueLabelRef = useRef(null);
  const progressRefCombined = useCombinedRef(ref, progressRef);
  const theme = useDesignSystemTheme();
  const minVisibleValueWidth = theme.Progress.minVisibleValueWidth;
  const progressSize = isCircle && size === ProgressSize.DEFAULT ? ProgressSize.MEDIUM : size;
  const resolvedColor = theme.Progress.iconColor;
  const resolveValueColor = valueColor ?? theme.Progress.progressValueColor;
  useLayoutEffect(() => {
    function onResize() {
      if (progressValueRef.current && progressRef.current) {
        const percentValue = value / max;
        const progressBoundingClientRect = progressRef.current.getBoundingClientRect();
        const minVisibleWidthCountdown = isCountdown ? minVisibleValueWidth * 1.5 : minVisibleValueWidth;
        const translationValue = getTranslateXTransformForValue(value, max);
        if (isValueVisible && minVisibleValueWidth > 0 && progressBoundingClientRect.width * percentValue <= minVisibleWidthCountdown) {
          progressValueRef.current.style.transform = `translateX(${translationValue})`;
          if (progressValueLabelRef.current) {
            const translationValueLabel = `calc(-100% + ${px(minVisibleWidthCountdown)})`;
            progressValueLabelRef.current.style.transform = `translateX(${translationValueLabel})`;
          }
        } else {
          progressValueRef.current.style.transform = `translateX(${translationValue})`;
          if (progressValueLabelRef.current) {
            progressValueLabelRef.current.style.transform = `translateX(${translationValue})`;
          }
        }
      }
    }
    onResize();
    if (minVisibleValueWidth > 0) {
      window.addEventListener('resize', onResize);
    }
    return () => {
      if (minVisibleValueWidth > 0) {
        window.removeEventListener('resize', onResize);
      }
    };
  }, [isValueVisible, max, value, isCountdown, minVisibleValueWidth]);
  const progressIndex = value / max;
  const checkColor = useMemo(() => {
    if (isDynamicColor) {
      if (isCountdown) {
        if (progressIndex > DynamicValues.SMALL) {
          return ProgressColor.SECONDARY_CONTRAST;
        } else if (progressIndex > DynamicValues.TINY) {
          return ProgressColor.ATTENTION;
        } else {
          return ProgressColor.PRIMARY_CONTRAST;
        }
      } else {
        if (progressIndex < 1 - DynamicValues.SMALL) {
          return ProgressColor.SECONDARY_CONTRAST;
        } else if (progressIndex < 1 - DynamicValues.TINY) {
          return ProgressColor.ATTENTION;
        } else {
          return ProgressColor.PRIMARY_CONTRAST;
        }
      }
    } else {
      return color;
    }
  }, [progressIndex, color, isDynamicColor, isCountdown]);
  return React.createElement(ProgressRoot, _extends({}, restProgressProps, {
    role: "progressbar",
    "aria-valuenow": value,
    "aria-valuemin": 0,
    "aria-valuemax": max,
    ref: progressRefCombined,
    tabIndex: -1,
    size: progressSize
  }, createDataTestId(testId), {
    "data-component": "Progress"
  }), isCircle ? React.createElement(Fragment, null, isValueVisible && React.createElement(ProgressValueCirlceLabel, {
    size: size
  }, React.createElement(Text, {
    as: "small",
    styledAs: SizeToStyle[size],
    color: resolveValueColor
  }, isCountdown ? React.createElement(Amount, {
    type: StyleType.COUNTDOWN,
    value: value
  }) : React.createElement(Amount, {
    type: StyleType.PERCENT,
    value: value / max,
    minFractionDigits: 0,
    maxFractionDigits: 2
  }))), !isValueVisible && !iconComponent && children && React.createElement(ProgressValueCirlceLabel, {
    size: size
  }, React.createElement(Text, {
    as: "small",
    styledAs: SizeToStyle[size],
    color: resolveValueColor
  }, children)), !isValueVisible && iconComponent && React.createElement(ProgressValueCirlceLabel, {
    size: size
  }, React.createElement(Icon, {
    iconComponent: iconComponent,
    primaryColor: resolvedColor,
    size: progressSize / 2
  })), React.createElement(Circle, {
    color: checkColor,
    value: value,
    max: max,
    isCountdown: isCountdown
  })) : React.createElement(Fragment, null, isValueVisible && React.createElement(ProgressValueLabel, {
    ref: progressValueLabelRef,
    value: value,
    max: max
  }, React.createElement(Text, {
    as: "small",
    color: resolveValueColor
  }, isCountdown ? React.createElement(Amount, {
    type: StyleType.COUNTDOWN,
    value: value
  }) : React.createElement(Amount, {
    type: StyleType.PERCENT,
    value: value / max,
    minFractionDigits: 0,
    maxFractionDigits: 2
  }))), React.createElement(ProgressBackground, null, React.createElement(ProgressValue, {
    color: checkColor,
    value: value / max,
    max: 100,
    ref: progressValueRef
  }))));
});